import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';
import type { AgentStatus, IAgent, IContract, IContractRequirement } from '@/store/types';
import { IContractSelect, LoaderWrapper } from '@/components/common';

import { ContractSelect, Drawer, Tab } from '@/components/common';
import { HistoryTable, RemoveAgentModal, SuspendAgentModal } from '@/components/client/contracts';
import { CrossEllipsed, Pause } from '@/assets/icons';
import { media } from '@/styles/theme';
import { getAgentContractHistory, getAgentsContractList, getContractsAgentList } from '@/store/slices/contractSlice';
import { useAppDispatch, useAppSelector, useModal, usePathParam } from '@/hooks';
import { AgentsComplianceTable } from './AgentsComplianceTable';
import { PathParams } from '@/store/enums';
import {
  selectCurrentAgent,
  selectIsPendingAction,
  selectIsPendingRestermActions,
} from '@/store/selectors/contractSelectors';
import { DisabledRestermInfo } from '@/components/client/contracts/agents/DisabledRestermInfo';
import { ContactDrawerHeaderSkeleton, ContactDrawerSelectSkeleton } from './ContractsDrawer.skeleton';
import { HistoryTableSkeleton } from '../history/HistoryTable.skeleton';
import { hasPermission } from '@/helpers/permissionValidator';
import { Permission, PermissionLevel } from '@/constants/client/permission';

interface IContractsDrawer {
  show: boolean;
  agent: IAgent | undefined;
  handleClose: () => void;
  requirements: IContractRequirement | undefined;
}

const contractsDrawerSelector = createSelector(
  selectCurrentAgent,
  selectIsPendingAction(getAgentContractHistory.typePrefix),
  selectIsPendingAction(getContractsAgentList.typePrefix),
  selectIsPendingRestermActions,
  (currentAgent, isLoadingAgentContractHistory, isLoadingContractsList, isPendingResterm) => ({
    currentAgent: currentAgent.active,
    agentHistory: currentAgent.history,
    isLoadingContractsList,
    isLoadingAgentContractHistory,
    isLoading: isLoadingContractsList || isLoadingAgentContractHistory,
    isPendingResterm,
  }),
);

const ContractsDrawer = ({ show, handleClose, agent, requirements }: IContractsDrawer): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    currentAgent,
    agentHistory,
    isLoading,
    isLoadingContractsList,
    isLoadingAgentContractHistory,
    isPendingResterm,
  } = useAppSelector(contractsDrawerSelector);
  const [contractId] = usePathParam(PathParams.contractId);
  const [, , clearWeekId] = usePathParam(PathParams.weekId);
  const [agentContractId, setAgentContractId, clearAgentContractId] = usePathParam(PathParams.spContractId);
  const [selectedTab, setSelectedTab, clearSelectedTab] = usePathParam<string>(PathParams.spTab);
  const [spId] = usePathParam(PathParams.spId);

  const { openModal, closeModal } = useModal();

  const [contractList, setContractList] = useState<IContract[] | null>(null);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const currentClientId = useAppSelector(state => state.auth.user?.currentClient?.clientId) ?? 0;

  const agentsContract: IContract | undefined = useMemo(
    () => contractList?.find(item => item.contractId === agentContractId),
    [agentContractId, contractList],
  );

  const onClose = () => {
    clearSelectedTab();
    clearAgentContractId();
    clearWeekId();
    handleClose();
  };

  const handleModalClose = (shouldDrawerClose?: boolean) => {
    closeModal();
    if (shouldDrawerClose === true) {
      setDrawerClosing(true);
      onClose();
      setDrawerClosing(false);
    }
  };

  const fetchAgentHistory = useCallback(() => {
    if (!agentContractId || !agent?.userId) return;
    return dispatch(getAgentContractHistory({ contractId: agentContractId, userId: agent.userId }));
  }, [agent?.userId, agentContractId, dispatch]);

  useEffect(() => {
    const getAgentContractHistoryCall = fetchAgentHistory();

    return () => getAgentContractHistoryCall?.abort();
  }, [fetchAgentHistory]);

  useEffect(() => {
    if (!show || drawerClosing) return;
    if (!selectedTab) setSelectedTab('compliance');
  }, [selectedTab, setSelectedTab, show]);

  useEffect(() => {
    if (!show || drawerClosing) return;
    if (!agentContractId && contractId) setAgentContractId(contractId);
  }, [agentContractId, contractId, setAgentContractId, show]);

  useEffect(() => {
    if (!currentAgent?.userId) return;

    const getAgentsContractListCall = dispatch(getAgentsContractList({ userId: currentAgent.userId }));

    getAgentsContractListCall.then(response => {
      if (response.meta.requestStatus === 'fulfilled') setContractList(response.payload as IContract[]);
    });

    return () => getAgentsContractListCall.abort();
  }, [currentAgent?.userId, dispatch]);

  const removeDisabled =
    !['active', 'suspended', 'reinstated'].some(item => currentAgent?.agentStatuses?.includes(item as AgentStatus)) ||
    agentContractId !== contractId ||
    isLoading;
  const suspendDisabled =
    !['active', 'reinstated'].some(item => currentAgent?.agentStatuses?.includes(item as AgentStatus)) ||
    agentContractId !== contractId ||
    isLoading;

  const contractSelects: IContractSelect[] | undefined = useMemo(() => {
    if (!contractList) return;

    return contractList.reduce<IContractSelect[]>((acc, contract) => {
      if (
        contract.clientId === currentClientId &&
        contract.isActive === 1 &&
        !acc.some(storedContract => storedContract.contractId === contract.contractId)
      ) {
        acc.push(contract);
      }
      return acc;
    }, []);
  }, [contractList]);

  const openRemoveModal = useCallback(
    () =>
      currentAgent &&
      openModal({
        title: t`clientGigs.modal.remove.title`,
        size: 'l',
        disableClose: isPendingResterm,
        content: (
          <RemoveAgentModal
            handleClose={handleModalClose}
            agent={currentAgent}
            contract={agentsContract}
            fetchAgent={fetchAgentHistory}
          />
        ),
      }),
    [agentsContract, handleModalClose, currentAgent, fetchAgentHistory, isPendingResterm, openModal, t],
  );

  const openSuspendModal = useCallback(
    () =>
      currentAgent &&
      openModal({
        title: t`clientGigs.modal.suspend.title`,
        size: 'l',
        disableClose: isPendingResterm,
        content: (
          <SuspendAgentModal
            handleClose={handleModalClose}
            agent={currentAgent}
            contract={agentsContract}
            fetchAgent={fetchAgentHistory}
          />
        ),
      }),
    [agentsContract, handleModalClose, currentAgent, fetchAgentHistory, isPendingResterm, openModal, t],
  );

  const visible = hasPermission(Permission.STAFFING, PermissionLevel.MODIFY) ? 'visible' : 'hidden';
  return (
    <Drawer
      show={show}
      handleClose={onClose}
      hideDropdown={contractId !== agentContractId}
      dropDownMenu={[
        {
          onClick: openRemoveModal,
          title: t`clientGigs.btnRemove`,
          hide: removeDisabled,
        },
        {
          onClick: openSuspendModal,
          title: t`clientGigs.btnSuspend`,
          hide: suspendDisabled,
        },
      ]}
    >
      <HeaderContainer>
        <LeftContainer>
          <LoaderWrapper isLoading={!currentAgent} loader={<ContactDrawerHeaderSkeleton />}>
            <Title>{`${currentAgent?.firstName} ${currentAgent?.lastName}`}</Title>
            {contractId !== agentContractId ? (
              <DisabledRestermInfo />
            ) : (
              <>
                <ActionButton onClick={openRemoveModal} disabled={removeDisabled} visibility={visible}>
                  <ActionButtonIcon icon={CrossEllipsed} />
                  <span>{t`clientGigs.btnRemove`}</span>
                </ActionButton>
                <ActionButton onClick={openSuspendModal} disabled={suspendDisabled} visibility={visible}>
                  <ActionButtonIcon icon={Pause} />
                  <span>{t`clientGigs.btnSuspend`}</span>
                </ActionButton>
              </>
            )}
          </LoaderWrapper>
        </LeftContainer>
        <Line hideOnDesktop />
        <LoaderWrapper isLoading={isLoadingContractsList || !contractSelects} loader={<ContactDrawerSelectSkeleton />}>
          <ContractSelect.Container className="select">
            <ContractSelect.Label style={{ fontWeight: 500 }}>{t`clientGigs.labelGig`}</ContractSelect.Label>
            <ContractSelect
              name="contracts"
              contractsList={contractSelects ?? []}
              externalHandling={{ contractId: agentContractId, setContractId: setAgentContractId }}
            />
          </ContractSelect.Container>
        </LoaderWrapper>
      </HeaderContainer>
      <Line />
      <TabsContainer>
        <Tab onSelect={key => key && setSelectedTab(key)} activeKey={selectedTab ?? 'compliance'}>
          <Tab.Item eventKey="compliance" title={t`clientGigs.tabCompliance`} tabClassName="tab-item">
            <ComplianceTableContainer>
              <AgentsComplianceTable
                userId={spId ?? currentAgent?.userId}
                contractId={agentContractId}
                requirements={requirements}
              />
            </ComplianceTableContainer>
          </Tab.Item>
          <Tab.Item eventKey="history" title={t`clientGigs.tabHistory`} tabClassName="tab-item">
            <LoaderWrapper isLoading={isLoadingAgentContractHistory || !agentHistory} loader={<HistoryTableSkeleton />}>
              <HistoryTable history={agentHistory ?? []} />
            </LoaderWrapper>
          </Tab.Item>
        </Tab>
      </TabsContainer>
    </Drawer>
  );
};

export { ContractsDrawer };

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${media.laptop} {
    flex-direction: row;
  }

  .select {
    margin-right: 0;

    ${media.laptop} {
      margin-right: 1.5rem;
    }
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${media.laptop} {
    flex-direction: row;
    align-items: center;
    padding-right: 2rem;
  }
`;

const Title = styled.div`
  margin-right: 14px;
  font-weight: 300;
  font-size: 24px;
  line-height: 140%;
  color: #374151;
  margin-bottom: 1rem;
  white-space: nowrap;

  ${media.tablet} {
    margin-bottom: 0;
  }
`;

const ActionButton = styled.button<{ visibility?: 'visible' | 'hidden' }>`
  display: none;
  align-items: center;
  height: 37px;
  padding-left: 22px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: #0087dd;
  border: none;
  background: none;
  visibility: ${props => (props.visibility === 'hidden' ? 'hidden' : 'visible')};

  &[disabled] {
    color: rgba(16, 16, 16, 0.3);
  }

  ${media.laptop} {
    display: flex;
  }
`;

const ActionButtonIcon = styled.div<{ icon: string }>`
  background-image: url(${({ icon }) => icon});
  background-repeat: no-repeat;
  background-position: center;
  width: 20px;
  height: 20px;
  margin-right: 7px;

  [disabled] & {
    filter: saturate(0%) opacity(70%);
  }
`;

const Line = styled.div<{ hideOnDesktop?: boolean }>`
  height: 1px;
  width: 100%;
  background-color: #dee2e6;
  margin-bottom: 17px;
  margin-top: 13px;

  ${({ hideOnDesktop }) =>
    hideOnDesktop &&
    css`
      display: block;

      ${media.laptop} {
        display: none;
      }
    `}
`;

const ComplianceTableContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 32px;
`;

const TabsContainer = styled.div`
  .tab-item {
    flex: 1;
    text-align: center;

    ${media.laptop} {
      flex: initial;
      text-align: left;
    }
  }
`;
